import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from "react";
import type { AuthContextValue, AuthUser } from "../types/auth";
import { logUserEvent } from "../modules/upload-inbox/api";

// ---------------------------------------------------------------------------
// Build-time constants injected by webpack DefinePlugin.
// ---------------------------------------------------------------------------
declare const __AUTH_PROVIDER__: string;
declare const __KEYCLOAK_URL__: string | undefined;
declare const __KEYCLOAK_REALM__: string | undefined;
declare const __KEYCLOAK_CLIENT_ID__: string | undefined;
declare const __AUTH0_DOMAIN__: string | undefined;
declare const __AUTH0_CLIENT_ID__: string | undefined;
declare const __AUTH0_AUDIENCE__: string | undefined;
declare const __AUTH0_REDIRECT_URI__: string | undefined;

// ---------------------------------------------------------------------------
// Determine active provider at build time.
// Webpack's DefinePlugin replaces the constant with a string literal, so the
// dead branch is tree-shaken out of the production bundle.
// ---------------------------------------------------------------------------
const AUTH_PROVIDER: "keycloak" | "auth0" =
  typeof __AUTH_PROVIDER__ !== "undefined" && __AUTH_PROVIDER__ === "auth0"
    ? "auth0"
    : "keycloak";

// ---------------------------------------------------------------------------
// Shared context default
// ---------------------------------------------------------------------------
const AuthContext = createContext<AuthContextValue>({
  user: null,
  authenticated: false,
  loading: true,
  login: () => {},
  logout: () => {},
  changePassword: () => {},
  signalAuthExpired: () => {},
  token: null,
  orgId: null,
  isPlatformAdmin: false,
});

/**
 * Sync the auth token to a window global so that remote MFEs loaded
 * via Module Federation can read it without sharing React context.
 */
function syncTokenToGlobal(token: string | null) {
  if (token) {
    (window as unknown as Record<string, string>).__HAVEN_TOKEN__ = token;
  } else {
    delete (window as unknown as Record<string, string | undefined>).__HAVEN_TOKEN__;
  }
}

// ===========================================================================
// KEYCLOAK PROVIDER
// ===========================================================================

import Keycloak from "keycloak-js";

const keycloak = new Keycloak({
  url: typeof __KEYCLOAK_URL__ !== "undefined" ? __KEYCLOAK_URL__ : "http://localhost:8080",
  realm: typeof __KEYCLOAK_REALM__ !== "undefined" ? __KEYCLOAK_REALM__ : "haven",
  clientId: typeof __KEYCLOAK_CLIENT_ID__ !== "undefined" ? __KEYCLOAK_CLIENT_ID__ : "haven-app",
});

function parseKeycloakUser(kc: Keycloak): AuthUser | null {
  if (!kc.authenticated || !kc.tokenParsed || !kc.token) return null;

  // Keycloak 26 uses lightweight access tokens — profile claims (name, email,
  // preferred_username) live in the ID token, not the access token.
  const id = (kc.idTokenParsed ?? {}) as Record<string, unknown>;
  const at = kc.tokenParsed as Record<string, unknown>;

  const name =
    (id.name as string) ||
    (id.preferred_username as string) ||
    (at.preferred_username as string) ||
    "";
  const email = (id.email as string) || (at.email as string) || "";

  const resourceAccess = (at.resource_access || id.resource_access) as
    | Record<string, { roles: string[] }>
    | undefined;
  const realmAccess = (at.realm_access || id.realm_access) as
    | { roles: string[] }
    | undefined;
  const topLevelRoles = (at.roles as string[]) || (id.roles as string[]) || [];

  const clientRoles = resourceAccess?.["haven-app"]?.roles || [];
  const realmRoles = realmAccess?.roles || [];
  const allRoles = [...new Set([...clientRoles, ...realmRoles, ...topLevelRoles])];

  const isPlatformAdmin =
    allRoles.includes("platform_admin") || allRoles.includes("admin");

  return {
    sub: (at.sub as string) ?? "",
    email,
    name,
    orgId: (at.org_id as string) || (id.org_id as string) || null,
    // JWT's org_name claim intentionally not exposed — see AuthUser type
    // for the reasoning and where to read the current org name instead.
    roles: allRoles,
    token: kc.token,
    isPlatformAdmin,
  };
}

const TOKEN_REFRESH_INTERVAL = 30_000; // 30s
const TOKEN_MIN_VALIDITY = 60; // seconds
/**
 * Max time to wait for keycloak.init() before assuming the SSO server is
 * unreachable and forcing a login redirect. Prevents the app hanging on the
 * splash screen when the network stalls partway through init.
 */
const AUTH_INIT_TIMEOUT_MS = 15_000;

/**
 * Force-redirect the user to the auth provider's login page. Used as the
 * hard fallback when init hangs or a session is torn down mid-flight.
 * Kept synchronous — safe to call from a timer or event listener.
 */
function forceKeycloakLoginRedirect() {
  try {
    // keycloak.login() returns a Promise but the underlying implementation
    // sets window.location, so calling it with a swallowed rejection is
    // still enough to trigger navigation. The .catch prevents unhandled
    // rejection warnings if the SDK rejects for any reason.
    const p = keycloak.login() as unknown;
    if (p && typeof (p as Promise<unknown>).catch === "function") {
      (p as Promise<unknown>).catch(() => {});
    }
  } catch (err) {
    console.error("keycloak.login() threw synchronously:", err);
  }
}

function KeycloakAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  // Debounce guard: multiple parts of the app can see the same expiration
  // (org fetch 401, refresh-timer failure, refocus check). Only redirect once.
  const [redirectingToLogin, setRedirectingToLogin] = useState(false);

  const beginLoginRedirect = useCallback(() => {
    if (redirectingToLogin) return;
    setRedirectingToLogin(true);
    setUser(null);
    syncTokenToGlobal(null);
    forceKeycloakLoginRedirect();
  }, [redirectingToLogin]);

  useEffect(() => {
    let refreshTimer: ReturnType<typeof setInterval> | undefined;
    let initTimeout: ReturnType<typeof setTimeout> | undefined;
    let cancelled = false;

    // Safety net: if init hangs (unreachable SSO, blocked iframe, DNS stall),
    // give up after AUTH_INIT_TIMEOUT_MS and force a login redirect rather
    // than leaving the shell parked on the splash screen forever.
    initTimeout = setTimeout(() => {
      if (cancelled || !loading) return;
      console.error(
        `Keycloak init did not complete within ${AUTH_INIT_TIMEOUT_MS}ms; forcing login redirect`
      );
      setLoading(false);
      forceKeycloakLoginRedirect();
    }, AUTH_INIT_TIMEOUT_MS);

    keycloak
      .init({
        onLoad: "login-required",
        pkceMethod: "S256",
        checkLoginIframe: false,
      })
      .then(() => {
        if (cancelled) return;
        if (initTimeout) clearTimeout(initTimeout);
        const parsed = parseKeycloakUser(keycloak);
        setUser(parsed);
        syncTokenToGlobal(parsed?.token ?? null);
        setLoading(false);

        refreshTimer = setInterval(() => {
          keycloak
            .updateToken(TOKEN_MIN_VALIDITY)
            .then((refreshed) => {
              if (refreshed) {
                const updated = parseKeycloakUser(keycloak);
                setUser(updated);
                syncTokenToGlobal(updated?.token ?? null);
              }
            })
            .catch(() => {
              // Clear local state BEFORE the redirect so ContextGuard's
              // "not authenticated → login" effect becomes a second line
              // of defence if the redirect itself races or is delayed.
              setUser(null);
              syncTokenToGlobal(null);
              forceKeycloakLoginRedirect();
            });
        }, TOKEN_REFRESH_INTERVAL);
      })
      .catch((err) => {
        if (cancelled) return;
        if (initTimeout) clearTimeout(initTimeout);
        console.error("Keycloak init failed:", err);
        setLoading(false);
        // Init failure with onLoad=login-required almost always means the
        // session is gone; redirect straight to login instead of leaving
        // the user on a spinning splash.
        forceKeycloakLoginRedirect();
      });

    return () => {
      cancelled = true;
      if (initTimeout) clearTimeout(initTimeout);
      if (refreshTimer) clearInterval(refreshTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallback(() => keycloak.login(), []);
  const logout = useCallback(() => {
    // Best-effort audit before the Keycloak logout triggers a redirect.
    // The audit fetch uses `keepalive`, so it survives navigation.
    // Clear the per-session flag so the next session logs a fresh login.
    try {
      sessionStorage.removeItem("haven.audit.login_logged");
      logUserEvent("logout");
    } catch {
      // Never let audit failures block logout.
    }
    return keycloak.logout({ redirectUri: window.location.origin });
  }, []);
  const changePassword = useCallback(
    () =>
      keycloak.login({
        action: "UPDATE_PASSWORD",
        redirectUri: window.location.origin + "/profile",
      }),
    []
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      authenticated: !!user,
      loading,
      login,
      logout,
      changePassword,
      signalAuthExpired: beginLoginRedirect,
      token: user?.token ?? null,
      orgId: user?.orgId ?? null,
      isPlatformAdmin: user?.isPlatformAdmin ?? false,
    }),
    [user, loading, login, logout, changePassword, beginLoginRedirect]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ===========================================================================
// AUTH0 PROVIDER
// ===========================================================================

import { Auth0Provider, useAuth0 } from "@auth0/auth0-react";

const auth0Domain =
  typeof __AUTH0_DOMAIN__ !== "undefined" ? __AUTH0_DOMAIN__ : "";
const auth0ClientId =
  typeof __AUTH0_CLIENT_ID__ !== "undefined" ? __AUTH0_CLIENT_ID__ : "";
const auth0Audience =
  typeof __AUTH0_AUDIENCE__ !== "undefined" ? __AUTH0_AUDIENCE__ : "";
const auth0RedirectUri =
  typeof __AUTH0_REDIRECT_URI__ !== "undefined" && __AUTH0_REDIRECT_URI__ !== ""
    ? __AUTH0_REDIRECT_URI__
    : window.location.origin;

/**
 * Inner component that reads from the Auth0 SDK hook and maps it to the
 * shared AuthContextValue shape.
 */
function Auth0ContextBridge({ children }: { children: ReactNode }) {
  const {
    isAuthenticated,
    isLoading,
    user: auth0User,
    loginWithRedirect,
    logout: auth0Logout,
    getAccessTokenSilently,
  } = useAuth0();

  const [token, setToken] = useState<string | null>(null);
  const [havenUser, setHavenUser] = useState<AuthUser | null>(null);
  // Debounce guard so the app doesn't fire loginWithRedirect twice in a burst
  // (e.g. a 401 lands right as the visibility handler also detects expiry).
  const [redirectingToLogin, setRedirectingToLogin] = useState(false);

  const beginLoginRedirect = useCallback(() => {
    if (redirectingToLogin) return;
    setRedirectingToLogin(true);
    setToken(null);
    setHavenUser(null);
    syncTokenToGlobal(null);
    loginWithRedirect({
      appState: { returnTo: window.location.pathname },
    }).catch((err) => console.error("loginWithRedirect failed", err));
  }, [redirectingToLogin, loginWithRedirect]);

  // Fetch the access token whenever auth state changes
  useEffect(() => {
    if (!isAuthenticated || !auth0User) {
      setToken(null);
      setHavenUser(null);
      syncTokenToGlobal(null);
      return;
    }

    getAccessTokenSilently({
      authorizationParams: { audience: auth0Audience },
    })
      .then((accessToken) => {
        setToken(accessToken);
        syncTokenToGlobal(accessToken);

        // Decode the access token to extract custom claims.
        // Auth0 stores roles in a custom namespace claim or permissions array.
        let payload: Record<string, unknown> = {};
        try {
          const base64Payload = accessToken.split(".")[1];
          payload = JSON.parse(atob(base64Payload));
        } catch {
          // Ignore decode errors — fall back to id_token claims
        }

        const permissions = (payload.permissions as string[]) || [];
        const namespacedRoles =
          (payload["https://haven.app/roles"] as string[]) || [];
        const topLevelRoles = (payload.roles as string[]) || [];
        const allRoles = [
          ...new Set([...namespacedRoles, ...topLevelRoles, ...permissions]),
        ];

        const isPlatformAdmin =
          allRoles.includes("platform_admin") || allRoles.includes("admin");

        const mapped: AuthUser = {
          sub: auth0User.sub ?? "",
          email: auth0User.email ?? "",
          name: auth0User.name ?? auth0User.nickname ?? "",
          // Auth0 org_id comes from the access token payload; org_name
          // intentionally not surfaced — see AuthUser type for why.
          orgId: (payload.org_id as string) || null,
          roles: allRoles,
          token: accessToken,
          isPlatformAdmin,
        };

        setHavenUser(mapped);
      })
      .catch((err) => {
        console.error("Auth0 getAccessTokenSilently failed:", err);
        // Silent refresh failure means the refresh token is dead or the SSO
        // session was revoked. Clear state and redirect to login so the user
        // isn't stranded on the splash screen.
        beginLoginRedirect();
      });
  }, [isAuthenticated, auth0User, getAccessTokenSilently, beginLoginRedirect]);

  // Refocus refresh: browsers throttle timers in backgrounded tabs, so a
  // scheduled refresh cadence isn't enough. When the tab comes back to the
  // foreground, immediately probe getAccessTokenSilently — if the refresh
  // token has died in the meantime, redirect to login now instead of waiting
  // for the next API call to 401.
  useEffect(() => {
    if (!isAuthenticated) return;
    let checking = false;
    const onVisible = () => {
      if (document.visibilityState !== "visible" || checking) return;
      checking = true;
      getAccessTokenSilently({
        authorizationParams: { audience: auth0Audience },
      })
        .then((accessToken) => {
          if (accessToken && accessToken !== token) {
            setToken(accessToken);
            syncTokenToGlobal(accessToken);
          }
        })
        .catch((err) => {
          console.error("Refocus token check failed", err);
          beginLoginRedirect();
        })
        .finally(() => {
          checking = false;
        });
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [isAuthenticated, getAccessTokenSilently, token, beginLoginRedirect]);

  const login = useCallback(
    () => loginWithRedirect({ appState: { returnTo: window.location.pathname } }),
    [loginWithRedirect]
  );

  const logout = useCallback(() => {
    try {
      sessionStorage.removeItem("haven.audit.login_logged");
      logUserEvent("logout");
    } catch {
      // Never let audit failures block logout.
    }
    return auth0Logout({
      logoutParams: { returnTo: window.location.origin },
    });
  }, [auth0Logout]);

  // Auth0 doesn't have a direct "change password" redirect like Keycloak.
  // Redirect to the Auth0 Universal Login change-password page instead.
  const changePassword = useCallback(
    () =>
      loginWithRedirect({
        authorizationParams: { prompt: "login", screen_hint: "change_password" },
      }),
    [loginWithRedirect]
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user: havenUser,
      authenticated: isAuthenticated && !!havenUser,
      loading: isLoading || (isAuthenticated && !havenUser),
      login,
      logout,
      changePassword,
      signalAuthExpired: beginLoginRedirect,
      token,
      orgId: havenUser?.orgId ?? null,
      isPlatformAdmin: havenUser?.isPlatformAdmin ?? false,
    }),
    [havenUser, isAuthenticated, isLoading, login, logout, changePassword, token, beginLoginRedirect]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function Auth0AuthProvider({ children }: { children: ReactNode }) {
  return (
    <Auth0Provider
      domain={auth0Domain}
      clientId={auth0ClientId}
      authorizationParams={{
        redirect_uri: auth0RedirectUri,
        audience: auth0Audience,
        scope: "openid profile email",
      }}
      useRefreshTokens
      cacheLocation="localstorage"
    >
      <Auth0ContextBridge>{children}</Auth0ContextBridge>
    </Auth0Provider>
  );
}

// ===========================================================================
// UNIFIED EXPORT — picks the right provider at build time
// ===========================================================================

/**
 * AuthProvider conditionally renders either the Keycloak or Auth0 adapter
 * based on the `__AUTH_PROVIDER__` build-time constant injected by webpack
 * DefinePlugin. The unused branch is tree-shaken from the production bundle.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  if (AUTH_PROVIDER === "auth0") {
    return <Auth0AuthProvider>{children}</Auth0AuthProvider>;
  }
  return <KeycloakAuthProvider>{children}</KeycloakAuthProvider>;
}

export function useAuth(): AuthContextValue {
  return useContext(AuthContext);
}

// Re-export for remotes to consume via shared context
export { AuthContext };
